import { useState, useEffect } from 'react';
import { Layout, Menu, Button, Dropdown, Avatar, Space, Typography, Drawer } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  TeamOutlined,
  DollarOutlined,
  PieChartOutlined,
  HistoryOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  HomeOutlined,
  CloseCircleOutlined,
  BankOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme/theme';
import { useAuth } from './AuthContext';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

interface AppShellProps {
  children: React.ReactNode;
  showSider?: boolean;
}

export function AppShell({ children, showSider = false }: AppShellProps) {
  const [collapsed, setCollapsed] = useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { t } = useTranslation('common');

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close drawer when route changes on mobile
  useEffect(() => {
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // User menu items
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'history',
      icon: <HistoryOutlined />,
      label: t('navigation.allocationHistory'),
      onClick: () => navigate('/history'),
    },
    {
      key: 'withdrawals',
      icon: <DollarOutlined />,
      label: t('navigation.withdrawals'),
      onClick: () => navigate('/withdrawals'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('navigation.settings'),
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('navigation.logout'),
      danger: true,
      onClick: handleLogout,
    },
  ];

  // Sidebar menu items (for admin sections)
  const siderMenuItems: MenuProps['items'] = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: <Link to="/">{t('navigation.dashboard')}</Link>,
    },
    ...(isAdmin
      ? [
          {
            key: '/admin',
            icon: <HomeOutlined />,
            label: <Link to="/admin">{t('navigation.admin')}</Link>,
          },
          {
            key: '/admin/profit-entry',
            icon: <DollarOutlined />,
            label: <Link to="/admin/profit-entry">{t('navigation.adminProfits')}</Link>,
          },
          {
            key: '/admin/withdrawals',
            icon: <DollarOutlined />,
            label: <Link to="/admin/withdrawals">{t('navigation.adminWithdrawals')}</Link>,
          },
          {
            key: '/admin/users',
            icon: <TeamOutlined />,
            label: <Link to="/admin/users">{t('navigation.adminUsers')}</Link>,
          },
          {
            key: '/admin/requests',
            icon: <CloseCircleOutlined />,
            label: <Link to="/admin/requests">Account Requests</Link>,
          },
          {
            key: '/admin/treasury',
            icon: <BankOutlined />,
            label: <Link to="/admin/treasury">Treasury</Link>,
          },
          {
            key: '/admin/reconciliation',
            icon: <SyncOutlined />,
            label: <Link to="/admin/reconciliation">Reconciliation</Link>,
          },
          {
            key: '/admin/contributions',
            icon: <DollarOutlined />,
            label: <Link to="/admin/contributions">Contributions</Link>,
          },
          {
            key: '/admin/settlements',
            icon: <ClockCircleOutlined />,
            label: <Link to="/admin/settlements">Settlements</Link>,
          },
          {
            key: '/admin/ownership',
            icon: <PieChartOutlined />,
            label: <Link to="/admin/ownership">Pool Ownership</Link>,
          },
          {
            key: '/admin/audit-logs',
            icon: <FileTextOutlined />,
            label: <Link to="/admin/audit-logs">Audit Logs</Link>,
          },
        ]
      : []),
  ];


  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 9999,
          padding: '8px',
          background: tokens.colorPrimary,
          color: 'white',
          textDecoration: 'none',
        }}
        onFocus={(e) => {
          e.currentTarget.style.left = '0';
        }}
        onBlur={(e) => {
          e.currentTarget.style.left = '-9999px';
        }}
      >
        Skip to content
      </a>

      {/* Sidebar - Desktop: fixed sidebar, Mobile: drawer */}
      {showSider && (
        isMobile ? (
          <Drawer
            placement="left"
            onClose={() => setMobileDrawerOpen(false)}
            open={mobileDrawerOpen}
            styles={{
              body: { padding: 0, background: 'rgba(0, 0, 0, 0.25)' },
              header: { display: 'none' }
            }}
            size={tokens.siderWidth}
          >
            <div
              style={{
                height: tokens.headerHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: tokens.spacing.base,
              }}
            >
              <Text strong style={{ color: 'white', fontSize: 18 }}>
                Admin
              </Text>
            </div>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[location.pathname]}
              items={siderMenuItems}
              style={{ background: 'transparent' }}
            />
          </Drawer>
        ) : (
          <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            width={tokens.siderWidth}
            collapsedWidth={tokens.siderCollapsedWidth}
            style={{
              overflow: 'auto',
              height: '100vh',
              position: 'fixed',
              left: 0,
              top: 0,
              bottom: 0,
            }}
          >
            <div
              style={{
                height: tokens.headerHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: tokens.spacing.base,
              }}
            >
              {!collapsed && (
                <Text strong style={{ color: 'white', fontSize: 18 }}>
                  Admin
                </Text>
              )}
            </div>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[location.pathname]}
              items={siderMenuItems}
            />
          </Sider>
        )
      )}

      <Layout style={{ marginLeft: showSider && !isMobile ? (collapsed ? tokens.siderCollapsedWidth : tokens.siderWidth) : 0 }}>
        {/* Header */}
        <Header
          style={{
            padding: isMobile ? `0 ${tokens.spacing.md}px` : `0 ${tokens.spacing.lg}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backdropFilter: 'blur(8px)',
          }}
        >
          <Space size={isMobile ? "middle" : "large"}>
            {showSider && (
              <Button
                type="text"
                icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
                onClick={() => isMobile ? setMobileDrawerOpen(true) : setCollapsed(!collapsed)}
                style={{
                  fontSize: '16px',
                  width: 48,
                  height: 48,
                  color: 'rgba(255, 255, 255, 0.85)',
                }}
              />
            )}
            <Space style={{ display: 'flex', alignItems: 'center'}}>
              <Link
                to="/"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                }}
              >
                <img 
                  src="/images/logo.png" 
                  alt="DivvyStash" 
                  style={{ 
                    height: isMobile ? '32px' : '40px',
                    width: 'auto',
                    display: 'block'
                  }} 
                />
              </Link>
              {isAdmin && !showSider && !isMobile && (
                <Link to="/admin">
                  <Button type="text" icon={<AppstoreOutlined />} style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    {t('navigation.admin')}
                  </Button>
                </Link>
              )}
            </Space>
          </Space>

          <Dropdown 
            menu={{ items: userMenuItems }} 
            placement="bottomRight" 
            arrow
            classNames={{ root: 'opaque-dropdown' }}
          >
            <Button
              type="text"
              style={{
                height: 48,
                padding: `0 ${tokens.spacing.sm}px`,
              }}
            >
              <Space>
                <Avatar icon={<UserOutlined />} style={{ backgroundColor: tokens.colorPrimary }} />
                {!isMobile && (
                  <Text style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    {user?.full_name || user?.email || 'User'}
                  </Text>
                )}
              </Space>
            </Button>
          </Dropdown>
        </Header>

        {/* Main Content */}
        <Content
          id="main-content"
          tabIndex={-1}
          style={{
            padding: isMobile ? tokens.spacing.md : tokens.spacing.lg,
            minHeight: 280,
            outline: 'none',
          }}
        >
          <div
            style={{
              maxWidth: tokens.maxContentWidth,
              margin: '0 auto',
            }}
          >
            {children}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}

export default AppShell;
